import {LatLng} from 'app/shared/map/interfaces';
import {decode as decodeFlexPolyline} from '@liberty-rider/flexpolyline';

/**
 * Utility for decoding HERE Flexible Polyline encoded strings
 *
 * HERE Flexible Polyline is a different format from Google's polyline encoding.
 * It includes a header with precision and other metadata.
 * The backend (GraphHopper with HERE routing) uses this format.
 */

/**
 * Decode a single encoded flexible polyline string to an array of LatLng coordinates
 * @param encoded - The encoded flexible polyline string from HERE/GraphHopper
 * @returns Array of LatLng coordinates
 */
export function decodePolyline(encoded: string): LatLng[] {
    if (!encoded) {
        return [];
    }

    try {
        // Use HERE flexible polyline decoder
        const result = decodeFlexPolyline(encoded);
        // The result contains polyline array with [lat, lng] or [lat, lng, altitude] tuples
        return result.polyline.map((point: number[]) => ({
            lat: point[0],
            lng: point[1]
        }));
    } catch (error) {
        console.error('Error decoding flexible polyline:', error, 'Input:', encoded);
        return [];
    }
}

/**
 * Decode multiple polyline segments (comma-separated) into a single array of coordinates
 * Used when a route has multiple segments stored as "polyline1,polyline2,polyline3"
 * @param encodedSegments - Comma-separated encoded polyline strings
 * @returns Array of LatLng coordinates for all segments combined
 */
export function decodeMultiplePolylines(encodedSegments: string): LatLng[][] {
    if (!encodedSegments) {
        return [];
    }

    const segments = encodedSegments.split(',');
    return segments.map((segment) => decodePolyline(segment.trim())).filter((coords) => coords.length > 0);
}

/**
 * Convert LatLng array to Leaflet-compatible format [[lat, lng], [lat, lng], ...]
 * @param coordinates - Array of LatLng objects
 * @returns Array of [lat, lng] tuples
 */
export function toLeafletCoordinates(coordinates: LatLng[]): [number, number][] {
    return coordinates.map((coord) => [coord.lat, coord.lng]);
}

/**
 * Convert GeoJSON polygon coordinates to LatLng array
 * GeoJSON format: [[[lat, lng], [lat, lng], ...]]
 * @param geoJsonCoordinates - GeoJSON polygon coordinates
 * @returns Array of LatLng coordinates
 */
export function geoJsonToLatLng(geoJsonCoordinates: number[][][]): LatLng[] {
    if (!geoJsonCoordinates || !geoJsonCoordinates[0]) {
        return [];
    }

    // GeoJSON stores coordinates as [lat, lng] based on your backend format
    return geoJsonCoordinates[0].map((coord) => ({
        lat: coord[0],
        lng: coord[1]
    }));
}

/**
 * Convert LatLng array to GeoJSON polygon format for saving to backend
 * @param coordinates - Array of LatLng coordinates
 * @returns GeoJSON polygon object
 */
export function latLngToGeoJson(coordinates: LatLng[]): {type: 'Polygon'; coordinates: number[][][]} {
    const geoJsonCoords = coordinates.map((coord) => [coord.lat, coord.lng]);

    // Close the polygon (first point = last point)
    if (geoJsonCoords.length > 0) {
        const first = geoJsonCoords[0];
        const last = geoJsonCoords[geoJsonCoords.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            geoJsonCoords.push([first[0], first[1]]);
        }
    }

    return {
        type: 'Polygon',
        coordinates: [geoJsonCoords]
    };
}
